import React, { useCallback, useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { FieldSet, Label, LabeledControl, Radio } from '@zillow/constellation-10';
import { debounce } from '@zg-rentals/zdash';

import WizardServerError from '../../../../model/WizardServerError';

import { ModularListingContext } from '../../../../containers/CreateListingPage/context';
import { WizardContext } from '../../../Wizard/context';

import { DISPLAY_SERVER_ERROR_AT } from '../../../../constants/WizardConstants';

import { useForm } from '../../../../hooks';

import { postingConstraints } from '../../../../utils/formValidation';
import PropertiesActions from '../../../../actions/PropertiesActions';
import PostingActions from '../../../../actions/PostingActions';
import actions from '../../../../containers/CreateListingPage/actions';
import wizardActions from '../../../Wizard/actions';

import { HOUSING_CONNECTOR_NETWORK_OPTIONS, HOUSING_CONNECTOR_NETWORK_OPTIONS_KEYS as OPTIONS } from '../constants';

import WizardAlert from '../../../WizardAlert';

import TimeConstants from '../../../../constants/TimeConstants';

const radioControlName = 'housingConnectorStatus';

const propTypes = {};
const defaultProps = {};

const HousingConnectorFieldSet = () => {
  const dispatch = useDispatch();

  const { reducerWizardDispatch } = useContext(WizardContext);
  const {
    modularListingState: { isInModalState, isSaveComplete, serverErrorObj, shouldSaveOnBlur },
    reducerDispatch,
  } = useContext(ModularListingContext);

  const {
    properties: {
      currentProperty: {
        housingConnectorStatus,
        isArchived,
        isFeed,
        isMultifamily,
        listingAlias,
        restrictions: { isUpdateListingRestricted },
      },
    },
    user: {
      features: { canUpdateListing },
    },
  } = useSelector((state) => state);

  const {
    inputs,
    hasFieldError,
    handleChange: handleFormChange,
  } = useForm(
    {
      housingConnectorStatus: housingConnectorStatus.toString(),
    },
    postingConstraints,
  );

  const isInputFormDisabled = isFeed || !canUpdateListing || isArchived || isUpdateListingRestricted;

  useEffect(() => {
    if (!isMultifamily && isSaveComplete) {
      // eslint-disable-next-line no-use-before-define
      save({
        target: {
          name: radioControlName,
          value: inputs.housingConnectorStatus,
        },
      });
    }
  }, [isSaveComplete]);

  const clearServerError = () => {
    if (serverErrorObj.fieldName) {
      actions.removeServerError({
        reducerDispatch,
      });
    }
  };

  const save = (event) => {
    // return if user must click on cta
    if (!shouldSaveOnBlur && !isSaveComplete) {
      return;
    }

    const { value, name: fieldName } = event.target;

    if (hasFieldError(fieldName, value)) {
      return;
    }

    const dataToSave = {
      fieldName,
      value,
      propertyId: listingAlias,
    };

    if (value === '') {
      // close the modal and not hit api to save if no choices were made but save btn was clicked
      actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });

      return;
    }

    wizardActions.updateIsFetchingData({ reducerWizardDispatch, isFetchingData: true });

    dispatch(PostingActions.property.edit(dataToSave))
      .then(() => {
        dispatch(PropertiesActions.updateCurrentProperty({ [fieldName]: value }));

        // reset save cta
        actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
      })
      .catch((message) => {
        updateServerError(message, fieldName);
      })
      .finally(() => {
        wizardActions.updateIsFetchingData({
          reducerWizardDispatch,
          isFetchingData: false,
        });
      });
  };

  const updateServerError = (message = '', fieldName = '') => {
    const displayAt = isInModalState ? DISPLAY_SERVER_ERROR_AT.FIELD : DISPLAY_SERVER_ERROR_AT.PAGE;

    actions.updateServerError({
      reducerDispatch,
      serverErrorObj: WizardServerError({
        displayAt,
        fieldName,
        message,
        displayMessage: message,
      }),
    });
  };

  const saveDebounced = useCallback(debounce(save, TimeConstants.SELECTION_DEBOUNCE_TIME_MS), []);

  const handleChange = (event) => {
    event.persist();
    clearServerError();
    handleFormChange(event);
    saveDebounced(event);
  };

  return (
    <div data-testid="HousingConnectorFormField">
      <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.FIELD} />

      <FieldSet disabled={isInputFormDisabled} data-testid="HousingConnectorFieldSet">
        <LabeledControl
          controlId="LabeledControl-HousingConnector1"
          label={<Label>{HOUSING_CONNECTOR_NETWORK_OPTIONS[OPTIONS.GLOBAL]}</Label>}
          control={
            <Radio
              name={radioControlName}
              value={OPTIONS.GLOBAL}
              checked={inputs.housingConnectorStatus === OPTIONS.GLOBAL}
              onChange={handleChange}
            />
          }
        />

        <LabeledControl
          controlId="LabeledControl-HousingConnector2"
          label={<Label>{HOUSING_CONNECTOR_NETWORK_OPTIONS[OPTIONS.DISABLED]}</Label>}
          control={
            <Radio
              name={radioControlName}
              value={OPTIONS.DISABLED}
              checked={inputs.housingConnectorStatus === OPTIONS.DISABLED}
              onChange={handleChange}
            />
          }
        />

        <LabeledControl
          controlId="LabeledControl-HousingConnector3"
          label={<Label>{HOUSING_CONNECTOR_NETWORK_OPTIONS[OPTIONS.EXCLUSIVE]}</Label>}
          control={
            <Radio
              name={radioControlName}
              value={OPTIONS.EXCLUSIVE}
              checked={inputs.housingConnectorStatus === OPTIONS.EXCLUSIVE}
              onChange={handleChange}
            />
          }
        />
      </FieldSet>
    </div>
  );
};

HousingConnectorFieldSet.propTypes = propTypes;
HousingConnectorFieldSet.defaultProps = defaultProps;

export default HousingConnectorFieldSet;
